import { CSSProperties, ReactNode, useContext, useEffect } from 'react';
import { styled } from '@stitches/configed';
import { PageHeader } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import RouteContext from './RouteContext';
import { RouterType } from '.';
import { PageLoading } from '..';
import { useNavContext } from './NavContext';

const Main = styled('div', {
  paddingLeft: 24,
  paddingRight: 24,
  width: 1280,
  maxWidth: '100%',
  // minHeight: '100vh',
  paddingBottom: 94, // padding for footer fixed: 76 height + 16 padding
  margin: 0,
  '@xs': {
    paddingBottom: 24, // remove padding for footer fixed
    height: 'calc(100% - 100px)', // minus padding-bottom
    margin: '24px auto 0', // top left-right bottom
    // maxWidth: 'calc(100% - 24px)', // fluid and min margin of auto
  },
});
const Header = styled('div', {
  marginTop: 0,
  marginBottom: 0,
  padding: '0px',
  '@xs': {
    // marginTop: 24,
    marginBottom: 24,
    // padding: '0 24px',
    // margin: '24px auto 0', // top left-right bottom
  },
});
const Footer = styled('div', {
  width: '100%',
  marginTop: 16,
  padding: 0,
  '@xs': {
    marginTop: 24,
    position: 'initial',
    width: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
  },
});
const FooterLeft = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  padding: '0 16px',
  '@xs': {
    padding: 0,
  },
});
const FooterRight = styled('div', {
  backgroundColor: '#fff',
  position: 'fixed',
  bottom: 0,
  width: '100%',
  padding: 16,
  borderTop: '1px solid $f0f0f0',
  boxShadow:
    '0 -6px 16px -8px rgb(0 0 0 / 8%), 0 -9px 28px 0 rgb(0 0 0 / 5%), 0 -12px 48px 16px rgb(0 0 0 / 3%)',
  '@xs': {
    display: 'flex',
    justifyContent: 'right',
    position: 'initial',
    width: 'auto',
    padding: 0,
    backgroundColor: 'transparent',
    borderTop: 0,
    boxShadow: 'none',
  },
});
const FooterStyled = styled('div', {
  position: 'fixed',
  bottom: 0,
  width: '100%',
  padding: 16,
  borderTop: '1px solid $f0f0f0',
  backgroundColor: '#fff',
  boxShadow:
    '0 -6px 16px -8px rgb(0 0 0 / 8%), 0 -9px 28px 0 rgb(0 0 0 / 5%), 0 -12px 48px 16px rgb(0 0 0 / 3%)',
  '@xs': {
    display: 'block',
    position: 'initial',
    width: 'auto',
    padding: 0,
    borderTop: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
});

type Props = {
  loading?: boolean;
  heading?: string;
  extraHeading?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  children: ReactNode;
  dynamicRoutesMap?: Record<
    string,
    {
      path?: string;
      name?: string;
    }
  >;
  maxWidth?: number;
  style?: CSSProperties;
  showBreadcrumb?: boolean;
  showGoBackArrow?: boolean;
  goBack?: string;
};
const PageContainer = ({
  loading,
  heading,
  style,
  extraHeading,
  header,
  footer,
  footerLeft,
  footerRight,
  children,
  dynamicRoutesMap,
  maxWidth,
  showBreadcrumb,
  showGoBackArrow,
  goBack,
}: Props) => {
  const context = useContext(RouteContext);
  const { setMobileRightHeader, setPageName } = context;

  const { setShowArrow, setGoBackExact } = useNavContext();

  useEffect(() => {
    setMobileRightHeader && setMobileRightHeader(extraHeading);
  }, [setMobileRightHeader, extraHeading]);

  useEffect(() => {
    setPageName && heading && setPageName(heading);
  }, [setPageName, heading]);

  useEffect(() => {
    if (!showGoBackArrow) setShowArrow && setShowArrow(false);
    showGoBackArrow && setShowArrow && setShowArrow(showGoBackArrow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGoBackArrow]);

  useEffect(() => {
    if (!goBack) setGoBackExact && setGoBackExact(undefined);
    goBack && setGoBackExact && setGoBackExact(goBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goBack]);

  const location = useLocation();
  const params = useParams();

  const pathSnippets = location.pathname.split('/').filter((i) => i);
  const f = (path: string) => {
    const rootRoute = context.routes?.find((x) => x.path === path);
    if (!rootRoute) return {};
    const map: Record<string, string> = {
      [path]: rootRoute?.name,
    };

    const convertToBreadcrumbMap = (
      parentPath: string,
      routes: RouterType[],
    ) => {
      routes.forEach((x) => {
        // remove reference array values.routes
        let path = x.path;
        let name = x.name;
        if (/^\/:/.test(path)) {
          const dynamicParam = params[path.replace('/:', '')];
          if (dynamicParam) path = '/' + dynamicParam;
        }
        if (dynamicRoutesMap) {
          const l = dynamicRoutesMap[x.path];
          if (l) {
            path = '/' + l.path || '';
            name = l.name || '';
          }
        }
        const newPath = parentPath + path;
        if (x.hideBreadcrumb) {
          hideBreadcrumb[newPath] = true;
        }
        map[newPath] = name;
        if (x.routes) {
          convertToBreadcrumbMap(newPath, x.routes);
        }
      });
    };

    // begin excute function
    if (rootRoute?.routes)
      convertToBreadcrumbMap(rootRoute.path, rootRoute.routes);
    return map;
  };
  const hideBreadcrumb: Record<string, boolean> = {};

  const breadcrumbNameMap = f('/' + pathSnippets[0]);
  const breadcrumbItems = pathSnippets.map((_, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    return {
      path: url,
      breadcrumbName: breadcrumbNameMap[url],
    };
  });
  return (
    <>
      {loading ? (
        <PageLoading />
      ) : (
        <>
          {showBreadcrumb &&
            !context.isMobile &&
            !hideBreadcrumb[location.pathname] &&
            breadcrumbItems &&
            breadcrumbItems.length > 1 && (
              <PageHeader
                ghost={false}
                // title={heading ? heading : values.pageName}
                breadcrumb={{
                  separator: '>',
                  routes: breadcrumbItems,
                  itemRender: (route, _params, routes, _paths) => {
                    const last = routes.indexOf(route) === routes.length - 1;
                    return last ? (
                      <span>{route.breadcrumbName}</span>
                    ) : (
                      <Link to={route.path}>{route.breadcrumbName}</Link>
                    );
                  },
                }}
              // extra={extraHeading}
              />
            )}
          <>
            <Main style={{ width: maxWidth, ...style }}>
              {header && <Header>{header}</Header>}
              {children}
              {footerLeft || footerRight || footer ? (
                <Footer>
                  {footerLeft && <FooterLeft>{footerLeft}</FooterLeft>}
                  {footer && <FooterStyled>{footer}</FooterStyled>}
                  {footerRight && <FooterRight>{footerRight}</FooterRight>}
                </Footer>
              ) : null}
              {/* 
              {footerLeft || footerRight ? (
                <Footer>
                  <FooterLeft>{footerLeft}</FooterLeft>
                  <FooterRight>{footerRight}</FooterRight>
                </Footer>
              ) : footer ? (
                <FooterStyled>XXX</FooterStyled>
              ) : null}*/}
            </Main>
          </>
        </>
      )}
    </>
  );
};

export default PageContainer;
